import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface ProductPrice {
  id: string;
  barcode: string | null;
  name: string;
  current_price: number | null;
  category: string | null;
  category_name: string | null;
  supplier: string | null;
  last_synced: string;
  square_item_id: string;
  description: string | null;
  inventory_tracking: boolean | null;
  current_stock: number | null;
}

interface Category {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const PRODUCTS_PER_PAGE = 50;

export const usePriceCheckQueries = (barcode: string, currentView: string, currentPage: number) => {
  // Query for product search with category names
  const { data: searchResults = [], refetch: searchProducts, isFetched: hasSearched } = useQuery({
    queryKey: ['product-search', barcode],
    queryFn: async () => {
      if (!barcode.trim()) return [];
      
      console.log('Searching for products with barcode:', barcode);
      const { data, error } = await supabase
        .from('product_catalog')
        .select(`
          *,
          square_categories!left(name)
        `)
        .or(`barcode.ilike.%${barcode}%,name.ilike.%${barcode}%`)
        .eq('is_active', true)
        .limit(20);

      if (error) {
        console.error('Search error:', error);
        throw error;
      }

      console.log('Search results:', data?.length || 0, 'products found');
      // Transform the data to include category_name
      return (data as any[]).map(item => ({
        ...item,
        category_name: item.square_categories?.name || null
      })) as ProductPrice[];
    },
    enabled: false, // Only run when manually triggered
  });

  // Query for total product count
  const { data: totalProductsCount = 0 } = useQuery({
    queryKey: ['total-products-count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('product_catalog')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (error) {
        console.error('Count error:', error);
        throw error;
      }

      console.log('Total products count:', count);
      return count || 0;
    },
    enabled: currentView === 'all-products'
  });

  // Query for paginated products with category names
  const { data: paginatedProducts = [], isLoading: productsLoading } = useQuery({
    queryKey: ['paginated-products', currentPage],
    queryFn: async () => {
      const from = currentPage * PRODUCTS_PER_PAGE;
      const to = from + PRODUCTS_PER_PAGE - 1;

      console.log(`Loading products page ${currentPage}, range ${from}-${to}`);
      const { data, error } = await supabase
        .from('product_catalog')
        .select(`
          *,
          square_categories!left(name)
        `)
        .eq('is_active', true)
        .order('name')
        .range(from, to);

      if (error) {
        console.error('Paginated products error:', error);
        throw error;
      }

      console.log('Loaded products:', data?.length || 0);
      // Transform the data to include category_name
      return (data as any[]).map(item => ({
        ...item,
        category_name: item.square_categories?.name || null
      })) as ProductPrice[];
    },
    enabled: currentView === 'all-products'
  });

  // Query for all categories - Always fetch categories regardless of current view
  const { data: allCategories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['all-categories'],
    queryFn: async () => {
      console.log('Loading categories...');
      
      // First, let's check if the table exists and has data
      const { data: tableCheck, error: tableError } = await supabase
        .from('square_categories')
        .select('count(*)', { count: 'exact' });
      
      if (tableError) {
        console.error('Table check error:', tableError);
      } else {
        console.log('Categories table count:', tableCheck);
      }
      
      const { data, error } = await supabase
        .from('square_categories')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) {
        console.error('Categories error:', error);
        throw error;
      }

      console.log('Categories loaded:', data?.length || 0, 'categories');
      console.log('Sample categories:', data?.slice(0, 3));
      return data as Category[];
    },
    // Remove the enabled condition to always load categories
  });

  return {
    searchResults,
    searchProducts,
    hasSearched,
    totalProductsCount,
    paginatedProducts,
    productsLoading,
    allCategories,
    categoriesLoading
  };
};
